import React, { useState, useEffect } from "react";
import SystemModal from "../Common/SystemModal";
import toast from "react-hot-toast";
import ajaxApprovals from "../../util/remote/ajaxApprovals";

// format currency (UGX with commas)
const formatCurrency = (num) => {
  if (num == null || num === "") return "-";
  return `UGX ${Number(num).toLocaleString()}`;
};

const RequisitionApprovalDetailsModal = ({ ReqID }) => {
  const [requisitionsData, setRequisitionsData] = useState(null);
  const [loading, setLoading] = useState(false);

  // fetch requisition + approval details
  const fetchRequisitionsList = async () => {
    setLoading(true);
    const data = { reqID: ReqID };
    setRequisitionsData(null);

    const server_response =
      await ajaxApprovals.getRequisitionDetailsAndApprovalDetails(data);

    if (server_response.status === "OK") {
      setRequisitionsData(server_response.details);
    } else {
      toast.error(server_response.message || "Failed to fetch details");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (ReqID) fetchRequisitionsList();
  }, [ReqID]);

  // ---- org defaults ----
  const orgInfo = {
    name: "LA_PROCURE AFRICA LIMITED",
    lines: [
      "P.O.BOX 28775 KAMPALA",
      "PLOT 79B BOMBO ROAD",
      "TEL: +256700000001",
      "EMAIL: info@laprocure.africa",
    ],
    logoSrc: `${process.env.PUBLIC_URL}/assets/img/brand/icon-light.png`,
    watermarkSrc: `${process.env.PUBLIC_URL}/assets/img/brand/icon-light.png`,
  };

  const requisition = requisitionsData?.requisition?.list?.[0] ?? {};
  const items = requisitionsData?.items ?? [];
  const approvers = requisitionsData?.approvers ?? [];

  // print function
  const Print = (e, controls) => {
    e.preventDefault();
    const html = document.getElementById("printablediv").innerHTML;
    const w = window.open("", "parent");
    w.document.open();
    w.document.write(`
      <html>
        <head>
          <title>Material Requisition</title>
          <style>${baseStyles(orgInfo.watermarkSrc)}</style>
        </head>
        <body>${html}</body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
    w.onafterprint = () => w.close();
    w.onfocus = () => {
      setTimeout(() => {
        if (!w.closed) w.close();
      }, 500);
    };
  };

  const RenderFooter = (controls) => (
    <>
      <button className="btn ripple btn-dark" onClick={controls.close}>
        Close
      </button>
      <button
        className="btn ripple btn-success"
        onClick={(e) => Print(e, controls)}
      >
        Print
      </button>
    </>
  );

  return (
    <SystemModal
      title="Requisition Details"
      id="model-requisition"
      size="xl"
      footer={RenderFooter}
    >
      {loading ? (
        <div className="text-center p-4">Loading...</div>
      ) : (
        <div id="printablediv">
          {/* ✅ Inject CSS correctly */}
          <style>{baseStyles(orgInfo.watermarkSrc)}</style>

          <div className="doc">
            <div className="doc-content">
              {/* Header */}
              <div className="doc-header">
                <div className="logo-wrap">
                  <img src={orgInfo.logoSrc} alt="Logo" />
                </div>
                <div className="org-text">
                  <div className="org-name">{orgInfo.name}</div>
                  {orgInfo.lines.map((l, i) => (
                    <div key={i} className="org-line">
                      {l}
                    </div>
                  ))}
                </div>
              </div>

              <div className="title">MATERIAL REQUISITION FORM</div>

              {/* Meta table */}
              <table className="meta">
                <tbody>
                  <tr>
                    <td>
                      <b>Date:</b> {requisition.date_created || "-"}
                    </td>
                    <td>
                      <b>Requisition ID:</b> {requisition.id || "-"}
                    </td>
                    <td>
                      <b>Status:</b>
                      {String(requisition.status) === "1"
                        ? "✅ Approved"
                        : "⏳ Pending"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Department:</b> {requisition.deptID || "-"}
                    </td>
                    <td>
                      <b>Prepared By:</b> {requisition.created_by || "-"}
                    </td>
                    <td>
                      <b>Estimated Total:</b>
                      {formatCurrency(requisition.est_total)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3">
                      <b>Narration:</b> {requisition.naration || "-"}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Items */}
              <table className="items">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item Description</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>Est. Unit cost</th>
                    <th>SubTotal</th>
                    <th>Actual Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((it, idx) => (
                      <tr key={it.id}>
                        <td>{idx + 1}</td>
                        <td>{it.item_name}</td>
                        <td>{it.unit}</td>
                        <td>{it.quantity}</td>
                        <td>{formatCurrency(it.est_unit_cost)}</td>
                        <td>{formatCurrency(it.sub_total)}</td>
                        <td>{formatCurrency(it.actual_cost)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        No items
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Totals */}
              <div className="totals">
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <b>Estimated Total</b>
                      </td>
                      <td>{formatCurrency(requisition.est_total)}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>Real Total</b>
                      </td>
                      <td>{formatCurrency(requisition.real_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Approvers */}
              {approvers.length > 0 && (
                <div className="mt-3">
                  <h6>Approvals</h6>
                  <table>
                    <thead>
                      <tr>
                        <th>Approver</th>
                        <th>Comment</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvers.map((a) => (
                        <tr key={a.id}>
                          <td>
                            {a.first_name} {a.last_name}
                          </td>
                          <td>{a.comment || "-"}</td>
                          <td>{a.date_created}</td>
                          <td>
                            {String(a.status) === "1"
                              ? "✅ Approved"
                              : "⏳ Pending"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="footer-print">
              Printed on: {new Date().toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </SystemModal>
  );
};

// shared CSS
const baseStyles = (watermarkSrc) => `
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; }
  .doc {
    position: relative;
    padding: 18mm 15mm;
    min-width: 210mm;
    max-width: 210mm; /* A4 width */
    margin: 0 auto;
    background: #fff;
    border: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }
  .doc::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url("${watermarkSrc}") no-repeat center center; !important
    background-size: 60%;
    opacity: 0.2; /* 👈 control watermark opacity here */
    z-index: 0;
  }
  .doc-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1; /* ensures content stays above watermark */
  }
  .doc-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 8px;
    position: relative;
    z-index: 2;
  }
  .logo-wrap img { width: 180px; height: 180px; object-fit: contain; }
  .org-text { flex: 1; text-align: center; }
  .org-name { font-size: 20px; font-weight: 700; letter-spacing: .5px; }
  .org-line { font-size: 12px; line-height: 1.2; }
  .title {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    margin: 6px 0 10px 0;
    position: relative; z-index: 2;
  }
  table { border-collapse: collapse; width: 100%; margin-top: 10px; position: relative; z-index: 2; }
  th, td { border: 1px solid #111; padding: 6px 8px; font-size: 12.5px; vertical-align: top; }
  th { background: #f2f2f2; text-align: left; }
  .meta { margin-bottom: 12px; }
  .meta td { border: 1px solid transparent; padding: 4px 0; font-size: 13px; }
  .meta tr td:first-child, .meta tr td:nth-child(2), .meta tr td:nth-child(3) { border: none; }
  .items th:nth-child(4) { width: 40%; }
  .totals { margin-top: 16px; width: 260px; }
  .totals td:first-child { width: 60%; }
  .signature { margin-top: auto; }
  .signature .line { margin-top: 16px; border-bottom: 1px solid #111; width: 60%; height: 20px; }
  .footer-print { margin-top: 18px; font-size: 11px; text-align: right; color: #374151; position: relative; z-index: 2; }
  @page { size: A4; margin: 12mm; }
`;
export default RequisitionApprovalDetailsModal;
